import React from "react";
import { Badge, Box, HStack, SimpleGrid, Stack, Text } from "@chakra-ui/react";
import { BarList, ChartCard, DataTable, ShareBar, StatusIcon } from "./parts";
import { useVizTokens } from "./tokens";
import { cents, count, dateTime, directionVerb, money } from "./format";

// Who sends, from which clinic and number, and how much of the spend this database has a record of.

function trackingWords(tracking) {
  if (tracking.recorded_since) {
    return `Who sent each text, and why, is recorded since ${dateTime(tracking.recorded_since)}. Texts before then show what their clinic records and wording tell.`;
  }
  return "Who sent each text, and why, is recorded from the next text sent with this version. Texts before then show what their clinic records and wording tell.";
}

// The numbers card's title, by the direction shown.
const NUMBER_TITLES = { inbound: "Receiving numbers", all: "Clinic numbers" };

export default function PeoplePanel({ report }) {
  const t = useVizTokens();
  const tracking = report.tracking;
  const verb = directionVerb(report);
  const peopleRows = report.people.map((p) => ({
    key: p.key,
    label: p.label,
    value: p.cost,
    valueLabel: money(p.cost),
    sub: `${count(p.texts)} texts${p.role ? ` · ${p.role}` : ""}`,
    color: p.kind === "person" ? undefined : t.other,
  }));
  const clinicRows = report.clinics.map((c) => ({
    key: c.key, label: c.label, value: c.cost, valueLabel: money(c.cost),
    sub: `${count(c.texts)} texts · ${c.texts ? (c.segments / c.texts).toFixed(1) : 0} segments each`,
    color: c.key.startsWith("_") ? t.other : undefined,
  }));
  // Every sent text on the account, by what this database holds of it, whichever scope is shown.
  const coverage = [
    { key: "recorded", label: "Recorded when sent (who and why)", value: tracking.recorded.cost, color: t.series[0] },
    { key: "in_records", label: "In a clinic's SMS records", value: tracking.in_records.cost, color: t.series[2] },
    { key: "outside", label: `No record in this database${report.include_outside ? "" : " (left out)"}`,
      value: tracking.outside.cost, color: t.other },
  ];

  return (
    <Stack spacing={4}>
      <ChartCard title="How much of the spend this database has a record of"
                 subtitle={`${money(tracking.estimated_texting)} estimated from each text · ${money(tracking.billed_texting)} on Twilio's bill`}
                 table={{
                   columns: [
                     { key: "label", label: "Texts" },
                     { key: "texts", label: "Count", numeric: true, render: (r) => count(r.texts) },
                     { key: "value", label: "Cost", numeric: true, render: (r) => money(r.value) },
                   ],
                   rows: coverage.map((c) => ({ ...c, texts: tracking[c.key].texts })),
                 }}>
        <ShareBar segments={coverage} format={money} />
        <HStack align="flex-start" spacing={2} mt={3}>
          <Box pt="2px"><StatusIcon status="info" size={14} /></Box>
          <Text fontSize="sm" color={t.textSecondary}>{trackingWords(tracking)}</Text>
        </HStack>
      </ChartCard>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <ChartCard title="Who is spending"
                   subtitle={`People in blue; automatic and unrecorded sends${verb === "sent" ? "" : ", and patients texting in,"} in gray`}
                   table={{
                     columns: [
                       { key: "label", label: "Who" },
                       { key: "role", label: "Role", render: (r) => r.role || "-" },
                       { key: "texts", label: "Texts", numeric: true, render: (r) => count(r.texts) },
                       { key: "segments", label: "Segments", numeric: true, render: (r) => count(r.segments) },
                       { key: "cost", label: "Cost", numeric: true, render: (r) => money(r.cost) },
                     ],
                     rows: report.people,
                   }}>
          <BarList rows={peopleRows} />
        </ChartCard>
        <ChartCard title="Spend by clinic" subtitle="From the clinic's SMS records or send events in this database"
                   table={{
                     columns: [
                       { key: "label", label: "Clinic" },
                       { key: "texts", label: "Texts", numeric: true, render: (r) => count(r.texts) },
                       { key: "segments", label: "Segments", numeric: true, render: (r) => count(r.segments) },
                       { key: "cost", label: "Cost", numeric: true, render: (r) => money(r.cost) },
                     ],
                     rows: report.clinics,
                   }}>
          <BarList rows={clinicRows} />
        </ChartCard>
      </SimpleGrid>

      <ChartCard title={NUMBER_TITLES[report.direction] || "Sending numbers"}
                 subtitle={`Every number on the Twilio account, and what it ${verb} in this range`}>
        <DataTable
          columns={[
            { key: "number", label: "Number" },
            { key: "kind", label: "Type", render: (r) => (r.kind === "toll-free" ? "Toll-free" : "Local") },
            { key: "clinic_label", label: "Used by", wrap: true },
            { key: "texts", label: "Texts", numeric: true, render: (r) => count(r.texts) },
            { key: "avg_segments", label: "Segments each", numeric: true },
            { key: "cost", label: "Texting cost", numeric: true, render: (r) => money(r.cost) },
            { key: "monthly_fee", label: "Monthly fee", numeric: true,
              render: (r) => (r.monthly_fee != null ? money(r.monthly_fee) : "-") },
            { key: "status", label: "", render: (r) => (r.texts === 0 && r.outside_texts > 0
              ? <Badge variant="subtle">{count(r.outside_texts)} texts outside this database</Badge>
              : r.owned && r.texts === 0
                ? <Badge colorScheme="orange" variant="subtle">{`Idle: paid for, ${verb} nothing`}</Badge>
                : !r.owned ? <Badge variant="subtle">Not on this account</Badge> : null) },
          ]}
          rows={report.numbers.map((n) => ({ ...n, key: n.number }))}
        />
        <Text fontSize="xs" color={t.muted} mt={2}>
          A segment costs {cents(report.billed.rates.segment)} from Twilio plus about{" "}
          {cents(report.billed.rates.carrier_fee)} in carrier fees.
        </Text>
      </ChartCard>
    </Stack>
  );
}
